import type { TFunction } from 'i18next';
import type { ApprovalAttributes } from '@/types/approval';
import {
  SERP_NOT_LOADED_MESSAGE,
  type SerpDocumentRef,
} from '@/utils/approval/serpDocument';

/** SERP 재고실사 조정 — 레거시 appr_edit_forms_serp_inventory_count */
export const SERP_STOCK_ADJUSTMENT_TYPE = 169;

export const isSerpStockAdjustmentType = (apvType: number): boolean =>
  apvType === SERP_STOCK_ADJUSTMENT_TYPE;

export type SerpStockInspectionRow = {
  SSN_CD?: string | null;
  STD_STK_QTY?: number | string | null;
  SALE_AMT?: number | string | null;
  STCKT_QTY?: number | string | null;
  STCKT_AMT?: number | string | null;
  GAP_QTY?: number | string | null;
  GAP_AMT?: number | string | null;
  LOSS_AMT?: number | string | null;
};

export type SerpStockSettlementRow = {
  SSN_CD?: string | null;
  LOSS_AMT?: number | string | null;
  GI_RAT?: number | string | null;
  DED_RTO?: number | string | null;
  STMT_AMT?: number | string | null;
  APRV_RMK?: string | null;
};

/** EAI 응답 원문 — 저장 attributes.data 에 손대지 않고 그대로 보관 */
export type SerpStockAdjustmentSource = {
  BRAND_NM?: string | null;
  SHOP_NM?: string | null;
  SM_SHOP_CD?: string | null;
  SM_SHOP_NM?: string | null;
  SM_SHOP_STS_DE?: string | null;
  SM_SHOP_BRN?: string | null;
  SM_SHOP_RMK?: string | null;
  STK_INVES_TYP_CD?: string | number | null;
  STK_INVES_TYP_NM?: string | null;
  STCKT_DE?: string | null;
  EXC_AMT?: number | string | null;
  SUM_LOSS_AMT?: number | string | null;
  SUM_STMT_AMT?: number | string | null;
  SM_TRD_GRT_AMT?: number | string | null;
  SM_SURETY_BOND_AMT?: number | string | null;
  RMK?: string | null;
  APRV_TITLE?: string | null;
  STK_INVES_LIST?: SerpStockInspectionRow[] | null;
  STMT_LIST?: SerpStockSettlementRow[] | null;
  [key: string]: unknown;
};

export type SerpStockAdjustmentDraftSource = {
  code: number;
  attributes: SerpDocumentRef;
  display: { source?: SerpStockAdjustmentSource | null };
};

export type SerpStockAdjustmentState = SerpDocumentRef & {
  source: SerpStockAdjustmentSource;
  /** SERP 응답으로 자동 입력한 제목 — 이탈 경고는 이 값과 비교(입력 칸이 없어 자동 제목만으론 작성 내용 아님). 편집은 미사용 */
  autoSubject: string;
};

/** 재고조사 유형 7 = 폐점 정산 — 거래보증금 줄을 보이고 미처리금액 줄은 숨김 */
export const isStoreClosureSettlement = (
  source: SerpStockAdjustmentSource,
): boolean => String(source.STK_INVES_TYP_CD ?? '').trim() === '7';

/** 값이 비면 빈 제목으로 덮어씀(레거시 동일) */
export const buildSerpStockAdjustmentSubject = (
  source: SerpStockAdjustmentSource,
  t: TFunction,
): string => {
  if (isStoreClosureSettlement(source)) {
    return source.SHOP_NM
      ? t('{{shop}} 매장 폐점 정산의 건', { shop: source.SHOP_NM })
      : '';
  }
  return source.APRV_TITLE ?? '';
};

export const formatSerpAmount = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return '';
  const num = Number(value);
  return Number.isFinite(num) ? num.toLocaleString('ko-KR') : String(value);
};

const asRows = <T>(raw: unknown): T[] => (Array.isArray(raw) ? raw : []);

export const getSerpInspectionRows = (source: SerpStockAdjustmentSource) =>
  asRows<SerpStockInspectionRow>(source.STK_INVES_LIST);

export const getSerpSettlementRows = (source: SerpStockAdjustmentSource) =>
  asRows<SerpStockSettlementRow>(source.STMT_LIST);

export const checkSerpStockAdjustmentBeforeSave = (
  state: SerpStockAdjustmentState | null | undefined,
): string | null => (state?.source ? null : SERP_NOT_LOADED_MESSAGE);

export const buildSerpStockAdjustmentAttributes = (
  state: SerpStockAdjustmentState | null | undefined,
  content: string,
): ApprovalAttributes | null =>
  state?.source
    ? {
        typeCode: state.typeCode,
        sequence: state.sequence,
        content,
        data: state.source,
      }
    : null;
